import React from 'react';
import { Search, X } from 'lucide-react';

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
  ariaLabel?: string;
}

/**
 * Standardized search input field with consistent styling and behavior
 * 
 * @param value - Current search value
 * @param onChange - Callback when value changes
 * @param placeholder - Placeholder text (defaults to "Search...")
 * @param className - Additional CSS classes for the container
 * @param showClearButton - Show X button to clear search (default: true)
 * @param size - Input size: 'sm', 'md' (default), or 'lg'
 */
const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  showClearButton = true,
  size = 'md',
  ariaLabel = 'Search'
}) => {
  const handleClear = () => {
    onChange('');
  };

  // Size-specific classes
  const sizeClasses = {
    sm: 'h-9 text-sm',
    md: 'h-10 text-base',
    lg: 'h-12 text-base'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-5 h-5'
  };

  const paddingClasses = {
    sm: 'pl-9 pr-9',
    md: 'pl-10 pr-10',
    lg: 'pl-12 pr-12'
  };

  return (
    <div className={`relative ${className}`} role="search">
      {/* Search Icon */}
      <Search 
        className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary ${iconSizeClasses[size]}`}
        aria-hidden="true"
      />
      
      {/* Input Field */}
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={`
          w-full ${paddingClasses[size]} ${sizeClasses[size]}
          border border-border-light dark:border-border-dark 
          rounded-lg 
          bg-white dark:bg-background-dark-secondary 
          text-text-primary dark:text-text-dark-primary 
          placeholder-text-tertiary dark:placeholder-text-dark-tertiary
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
          transition-colors
        `}
      />
      
      {/* Clear Button */}
      {showClearButton && value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary transition-colors"
          aria-label="Clear search"
        >
          <X className={iconSizeClasses[size]} />
        </button>
      )}
    </div>
  );
};

export default SearchField;

